/**
 * Stock domain API namespace — Stock Management → Shopify Stock.
 *
 * Pushes a brand's Product Sales closing qty to Shopify's on-hand quantity.
 * Admin only on the server; a preview reads Shopify and writes nothing, and a
 * push writes only when the server has SHOPIFY_STOCK_PUSH_ENABLED=true and the
 * app holds write_inventory.
 *
 * Follows the per-domain barrel migration direction (services/apiClient.js +
 * services/<domain>/api.js), re-exported from services/api.js.
 */
import api from '../apiClient';

// Shopify reads walk the catalogue a batch at a time, so these outlast the
// client's 60s default the same way the price push does.
const LONG = { timeout: 300000 };

export const shopifyStockAPI = {
  // What the page may do: the push switch, BI's Shopify inventory scopes, the
  // store's locations and the configured default.
  getStatus: () => api.get('/shopify-stock/status'),
  // BI's latest closing qty against what Shopify holds, per brand and in
  // total. Reads BI's own copy — no Shopify call, so it opens instantly.
  // sku / ean / name match on "contains"; ranges and types are value lists
  // from the response's own `options`.
  // Every filter the Dashboard carries has to be named here: the params object
  // is rebuilt from this list, so a key missing from it is dropped before the
  // request rather than ignored by the server.
  getOverview: ({
    supplierId, brandId, locationId, sku, ean, name, ranges, types, status,
    gaMin, gaMax, bdMin, bdMax, gaStatus, focusStatus, excludeNoGa,
  } = {}) =>
    api.get('/shopify-stock/overview', {
      params: {
        supplierId, brandId, locationId, sku, ean, name, ranges, types, status,
        gaMin, gaMax, bdMin, bdMax, gaStatus, focusStatus, excludeNoGa,
      },
    }),
  // Every SKU row behind the Dashboard across the brands in scope, with the
  // same filters and card selection, for the SKU table's Excel download.
  getSkus: ({
    supplierId, brandId, locationId, sku, ean, name, ranges, types, status,
    gaMin, gaMax, bdMin, bdMax, gaStatus, focusStatus, excludeNoGa,
  } = {}) =>
    api.get('/shopify-stock/overview/skus', {
      params: {
        supplierId, brandId, locationId, sku, ean, name, ranges, types, status,
        gaMin, gaMax, bdMin, bdMax, gaStatus, focusStatus, excludeNoGa,
      },
      ...LONG,
    }),
  // The Dashboard's per-SKU breakdown for one brand, for an expanded row. A
  // vendor row (brandId "vendor:<key>": unmapped variants whose Shopify vendor
  // names no BI brand) has no brand id, so it reads /overview/skus by vendor.
  getBrandSkus: (brandId, {
    locationId, sku, ean, name, ranges, types, status,
    gaMin, gaMax, bdMin, bdMax, gaStatus, focusStatus, excludeNoGa,
  } = {}) => {
    const vendor = String(brandId).startsWith('vendor:') ? String(brandId).slice(7) : null;
    return api.get(vendor ? '/shopify-stock/overview/skus' : `/shopify-stock/brands/${brandId}/skus`, {
      params: {
        vendor: vendor || undefined,
        locationId, sku, ean, name, ranges, types, status,
        gaMin, gaMax, bdMin, bdMax, gaStatus, focusStatus, excludeNoGa,
      },
    });
  },
  // Re-read Shopify (the whole catalogue, plus per-location quantities when BI
  // holds read_inventory) and return the rebuilt comparison. Walks ~20k
  // variants, so it outlasts the client's 60s default by a wide margin.
  sync: ({ locationId } = {}) => api.post('/shopify-stock/sync', { locationId }, { timeout: 600000 }),
  // Brands that have Product Sales rows, with their supplier, for the filters.
  getBrands: () => api.get('/shopify-stock/brands'),
  getPeriods: (brandId) => api.get(`/shopify-stock/brands/${brandId}/periods`),
  // One row per Product Sales item of the period. Reads BI's own copy of the
  // Shopify quantities — no Shopify call.
  getRows: (brandId, { year, month, locationId } = {}) =>
    api.get(`/shopify-stock/brands/${brandId}/rows`, { params: { year, month, locationId } }),
  // Reads Shopify's live quantities and reports what would change.
  preview: (brandId, { year, month, skus, locationId }) =>
    api.post(`/shopify-stock/brands/${brandId}/preview`, { year, month, skus, locationId }, LONG),
  push: (brandId, { year, month, skus, locationId }) =>
    api.post(`/shopify-stock/brands/${brandId}/push`, { year, month, skus, locationId }, LONG),
  // Re-read this brand's Shopify stock at the location into BI's copy.
  syncBrand: (brandId, { locationId }) =>
    api.post(`/shopify-stock/brands/${brandId}/sync-brand`, { locationId }, LONG),
  getLog: (brandId) => api.get(`/shopify-stock/brands/${brandId}/log`),
};

/**
 * Stock Management → InvenTree Parts List.
 *
 * InvenTree's whole parts master (~85k), the only source of sku_status and
 * phase_out_sku. A different collection from the purchase-order-derived parts
 * the Shopify matcher falls back on.
 */
export const inventreePartsAPI = {
  // One page of parts. Every filter has to be named in the destructure as well
  // as the params object, the same rule the Shopify Stock calls above follow.
  getCatalog: ({ page, limit, brand, supplier, status, productLine, replaces, search } = {}) =>
    api.get('/inventree-parts/catalog', {
      params: { page, limit, brand, supplier, status, productLine, replaces, search },
    }),
  // Counts, the sync date and the filter option lists, in one call.
  getCatalogStats: () => api.get('/inventree-parts/catalog/stats'),
  // Starts a run and returns at once — a walk over 85k parts outlasts any
  // request timeout, so the page polls the stats for the new date.
  syncCatalog: () => api.post('/inventree-parts/catalog/sync'),
};

export default shopifyStockAPI;
